import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { RUNTIME_ENV } from '../../config/runtimeEnv';

const SITE_URL = RUNTIME_ENV.domains.web.replace(/\/$/, '');
const SITE_NAME = 'BTMALL';
const DEFAULT_IMAGE = `${SITE_URL}/favicon-btmall.png`;

const publicPathPatterns = [
  /^\/$/,
  /^\/home-v[23]$/,
  /^\/about$/,
  /^\/products$/,
  /^\/promotions$/,
  /^\/support$/,
  /^\/policy(?:\/|$)/,
  /^\/news(?:\/|$)/,
  /^\/article\//,
  /^\/product\/detail\//,
  /^\/category\//,
  /^\/menu-category\//,
];

const titles = {
  vi: {
    '/': 'BTMALL | Mua sắm cùng BTMALL',
    '/about': 'Giới thiệu | BTMALL',
    '/products': 'Sản phẩm nổi bật | BTMALL',
    '/promotions': 'Khuyến mãi | BTMALL',
    '/support': 'Hỗ trợ khách hàng | BTMALL',
    '/news': 'Tin tức và kiến thức | BTMALL',
  },
  en: {
    '/': 'BTMALL | Shop with BTMALL',
    '/about': 'About us | BTMALL',
    '/products': 'Featured products | BTMALL',
    '/promotions': 'Promotions | BTMALL',
    '/support': 'Customer support | BTMALL',
    '/news': 'News and insights | BTMALL',
  },
  zh: {
    '/': 'BTMALL | 在线购物平台',
    '/about': '关于我们 | BTMALL',
    '/products': '精选产品 | BTMALL',
    '/promotions': '促销活动 | BTMALL',
    '/support': '客户支持 | BTMALL',
    '/news': '新闻与知识 | BTMALL',
  },
};

const descriptions = {
  vi: 'Mua sắm sản phẩm gia dụng, thời trang và chăn ga gối chính hãng tại BTMALL.',
  en: 'Shop authentic home appliances, fashion, blankets and bedding at BTMALL.',
  zh: '在BTMALL选购正品家电、时尚商品、毛毯和床上用品。',
};

function SeoRouteManager({ location }) {
  const { language } = useTranslation();
  const path = location.pathname.replace(/\/+$/, '') || '/';
  const indexable = publicPathPatterns.some(pattern => pattern.test(path));
  const canonical = `${SITE_URL}${path}`;
  const title = (titles[language] && titles[language][path]) || SITE_NAME;
  const description = descriptions[language] || descriptions.vi;
  const locale = language === 'zh' ? 'zh_CN' : language === 'en' ? 'en_US' : 'vi_VN';

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-WN74FEQ6PN', {
        page_path: `${location.pathname}${location.search}`,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon-btmall.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+84-868-965-519',
      contactType: 'customer service',
      availableLanguage: ['Vietnamese', 'English', 'Chinese'],
    },
  };

  return (
    <Helmet htmlAttributes={{ lang: language === 'zh' ? 'zh-CN' : language }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={indexable ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1' : 'noindex,nofollow'} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
    </Helmet>
  );
}

export default withRouter(SeoRouteManager);
